import React, { useState } from 'react';
import { Bell, Building2, Eye, Globe, Megaphone, Send, Undo2 } from 'lucide-react';
import api, { errorText } from '../services/api';
import { useApi } from '../lib/useApi';
import { useAccess } from '../context/AuthContext';
import { ago, dateTime, num } from '../lib/format';
import { Button, Card, Chip, Empty, ErrorBanner, Field, Input, Modal, PageHeader, PageSkeleton, Segmented, Select, Textarea, Tone, useToast } from '../components/ui';

interface Item {
  id: string; title: string; message: string; priority: 'low' | 'medium' | 'high'; target_type: 'all' | 'specific';
  tenant_id: string | null; tenant: string | null; sent_at: string; expires_at: string | null; state: 'live' | 'expired' | 'withdrawn';
  sent_by: string | null; reads: number; audience: number;
}
const PRIORITY_TONE: Record<string, Tone> = { high: 'red', medium: 'amber', low: 'slate' };
const STATE_TONE: Record<string, Tone> = { live: 'green', expired: 'slate', withdrawn: 'slate' };

export default function Notifications() {
  const toast = useToast();
  const { canWrite } = useAccess();
  const [state, setState] = useState<'all' | 'live' | 'expired' | 'withdrawn'>('all');
  const [composing, setComposing] = useState(false);
  const { data, error, loading, reload } = useApi<{ items: Item[]; counts: Record<string, number> }>('/notifications');

  if (loading) return <PageSkeleton />;
  if (error || !data) return <ErrorBanner text={error || 'Could not load notifications'} onRetry={reload} />;
  const items = data.items.filter(i => state === 'all' || i.state === state);

  const withdraw = async (i: Item) => {
    try { await api.post(`/notifications/${i.id}/withdraw`); toast('ok', `"${i.title}" was withdrawn`); reload(); }
    catch (e) { toast('error', errorText(e)); }
  };

  return (
    <div>
      <PageHeader title="Notifications" subtitle="Messages shown in the notification bell of every tenant, or one tenant"
        actions={canWrite ? <Button variant="primary" icon={Send} onClick={() => setComposing(true)}>New notification</Button> : undefined} />
      <div className="mb-4">
        <Segmented value={state} onChange={setState} options={[
          { key: 'all', label: 'All', count: data.items.length }, { key: 'live', label: 'Live', count: data.counts.live },
          { key: 'expired', label: 'Expired', count: data.counts.expired }, { key: 'withdrawn', label: 'Withdrawn', count: data.counts.withdrawn }]} />
      </div>
      <Card bodyClass="p-0">
        {items.length === 0 ? (
          <Empty icon={Megaphone} title="No notifications here" text="Send one to tell tenants about maintenance, new features or account issues."
            action={canWrite ? <Button variant="primary" icon={Send} onClick={() => setComposing(true)}>New notification</Button> : undefined} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map(i => {
              const readPct = i.audience ? Math.round(i.reads / i.audience * 100) : 0;
              return (
                <li key={i.id} className="flex flex-wrap items-start gap-4 px-5 py-4">
                  <span className={`mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg ${i.target_type === 'all' ? 'bg-blue-50 text-blue-700' : 'bg-violet-50 text-violet-700'}`}>
                    {i.target_type === 'all' ? <Globe className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">{i.title}</span>
                      <Chip tone={PRIORITY_TONE[i.priority]}>{i.priority}</Chip>
                      <Chip tone={STATE_TONE[i.state]} dot>{i.state}</Chip>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-600">{i.message}</p>
                    <div className="mt-1.5 text-xs text-slate-400">
                      To {i.target_type === 'all' ? 'all tenants' : i.tenant || 'a deleted tenant'} · sent {ago(i.sent_at)}{i.sent_by ? ` by ${i.sent_by}` : ''}
                      {i.expires_at && ` · ${i.state === 'expired' ? 'expired' : 'expires'} ${dateTime(i.expires_at)}`}
                    </div>
                  </div>
                  <div className="w-36 text-right">
                    <div className="flex items-center justify-end gap-1 text-xs text-slate-500"><Eye className="h-3.5 w-3.5" />{num(i.reads)} of {num(i.audience)} read</div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-blue-500" style={{ width: `${readPct}%` }} /></div>
                    {canWrite && i.state === 'live' && <Button size="sm" variant="ghost" icon={Undo2} className="mt-2" onClick={() => withdraw(i)}>Withdraw</Button>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
      {composing && <Compose onClose={() => setComposing(false)} onSent={() => { setComposing(false); reload(); }} />}
    </div>
  );
}

function Compose({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const toast = useToast();
  const tenants = useApi<{ tenants: { id: string; company_name: string; is_active: boolean }[] }>('/tenants');
  const [f, setF] = useState({ title: '', message: '', priority: 'medium', target_type: 'all', tenant_id: '', expires_at: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const valid = f.title.trim().length >= 3 && f.message.trim().length >= 5 && (f.target_type === 'all' || f.tenant_id);

  const send = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post('/notifications', { ...f, title: f.title.trim(), message: f.message.trim(),
        tenant_id: f.target_type === 'specific' ? f.tenant_id : null, expires_at: f.expires_at || null });
      toast('ok', 'Notification sent'); onSent();
    } catch (e) { setErr(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open title="New notification" onClose={onClose} width="max-w-xl"
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" icon={Send} loading={busy} disabled={!valid} onClick={send}>Send</Button></>}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Send to"><Select value={f.target_type} onChange={e => setF({ ...f, target_type: e.target.value })}><option value="all">All tenants</option><option value="specific">One tenant</option></Select></Field>
          <Field label="Priority"><Select value={f.priority} onChange={e => setF({ ...f, priority: e.target.value })}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></Select></Field>
        </div>
        {f.target_type === 'specific' && (
          <Field label="Tenant"><Select value={f.tenant_id} onChange={e => setF({ ...f, tenant_id: e.target.value })}>
            <option value="">Choose a tenant</option>
            {(tenants.data?.tenants || []).map(t => <option key={t.id} value={t.id}>{t.company_name}{t.is_active ? '' : ' (deactivated)'}</option>)}
          </Select></Field>
        )}
        <Field label="Title"><Input value={f.title} maxLength={120} onChange={e => setF({ ...f, title: e.target.value })} placeholder="Scheduled maintenance on Saturday" /></Field>
        <Field label="Message" hint={`${f.message.length}/2000`}><Textarea value={f.message} maxLength={2000} onChange={e => setF({ ...f, message: e.target.value })}
          placeholder="FluxWatch will be unavailable from 02:00 to 03:00 UTC while we upgrade the database." /></Field>
        <Field label="Hide after (optional)" hint="Leave empty to keep it until you withdraw it" error={err}>
          <Input type="datetime-local" value={f.expires_at} onChange={e => setF({ ...f, expires_at: e.target.value })} />
        </Field>
        <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600"><Bell className="h-3.5 w-3.5" />Appears in the bell of every active user {f.target_type === 'all' ? 'on the platform' : 'in that workspace'}.</div>
      </div>
    </Modal>
  );
}
